/**
 * v7.89 — Espelho local de operadores (servidor = fonte única da verdade).
 *
 * - `refreshOperatorsMirror()`: baixa a lista do servidor (com pinHash
 *   bcrypt) e SUBSTITUI a tabela Dexie `operators` — qualquer fantasma
 *   local é eliminado a cada sincronização.
 * - `verifyPinOffline(pin)`: valida o PIN contra os hashes locais via
 *   bcryptjs — login continua funcionando sem internet após a 1ª sync.
 */
import db from '@/db';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export async function refreshOperatorsMirror() {
  if (!API_URL) return { ok: false, reason: 'no_api_url' };
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 8000);
    const resp = await fetch(`${API_URL}/api/operators/mirror`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!resp.ok) return { ok: false, status: resp.status };
    const body = await resp.json();
    if (!body?.ok || !Array.isArray(body.operators)) return { ok: false };
    await db.transaction('rw', db.operators, async () => {
      await db.operators.clear();
      if (body.operators.length) {
        await db.operators.bulkAdd(body.operators.map((o) => ({
          opId: o.opId,
          name: o.name,
          role: o.role,
          active: o.active !== false,
          whatsapp: o.whatsapp || '',
          isMaster: !!o.isMaster,
          pinHash: o.pinHash || '',
          pinLength: o.pinLength || 0,
          updatedAt: o.updatedAt,
        })));
      }
    });
    return { ok: true, count: body.operators.length };
  } catch (e) {
    // Offline/timeout — mantém o espelho atual (login offline segue válido)
    return { ok: false, error: String(e?.message || e) };
  }
}

export async function verifyPinOffline(pin) {
  const bcrypt = (await import('bcryptjs')).default;
  const ops = await db.operators.toArray();
  const byLength = (o) => !o.pinLength || o.pinLength === pin.length;
  // Ativos primeiro; inativos depois (para retornar erro 'inactive' correto)
  const actives = ops.filter((o) => o.pinHash && o.active !== false && byLength(o));
  const inactives = ops.filter((o) => o.pinHash && o.active === false && byLength(o));
  for (const op of [...actives, ...inactives]) {
    // eslint-disable-next-line no-await-in-loop
    const match = await bcrypt.compare(pin, op.pinHash);
    if (match) return op;
  }
  return null;
}
